import {useEffect, useRef, useState, useContext} from "react";
import {source, layer, data as atlasData, HtmlMarker, Popup} from "azure-maps-control";
import AuthContext from "context/AuthContext";
import Loader from "components/loaders/Loader";
import DurationDisplay from "components/datestimes/DurationDisplay";
import AzureMapBase from "components/maps/azure/AzureMapBase";
import {getWasteIconHTML, getFontAwesomeIconHTML} from "components/waste-icons/svgs/getMarkerHTML";
import axiApi from "ApiCaller";
import L from "leaflet";

const AzureMapsRouteCalculator = ({
    waypoints,
    setWaypoints,
    center,
    hasChanged,
    setHasChanged,
    geojson,
    setGeojson,
    duration,
    setDuration,
    highlightedPoint,
    tF,
    wasteType: routeWasteType,
}) => {
    const {panel} = useContext(AuthContext);
    const [map, setMap] = useState(null);
    const [loading, setLoading] = useState(false);
    const linesSourceRef = useRef(null);
    const sharedPopup = useRef(new Popup({pixelOffset: [0, -30]}));
    const markersRef = useRef({});
    const htmlContentRef = useRef({});
    const abortControllerRef = useRef(null);

    useEffect(() => {
        if (map && waypoints.length > 0) {
            // Initial markers paint or route repaint if geojson exists
            addAssetsMap();
            if (geojson && Object.keys(geojson).length > 0) {
                paintRoute(geojson);
            } else {
                paintRoute(null); // Just show markers
            }
        }
    }, [map, waypoints]);

    useEffect(() => {
        if (hasChanged) {
            linesSourceRef.current?.clear();
        }
    }, [hasChanged]);

    const calculateRoute = async () => {
        if (!map || waypoints.length < 2) return;

        setLoading(true);
        try {
            const coords = waypoints.map((point) => [point.lng, point.lat]);
            const resp = await axiApi.post("/api/distribution/route/travel/", {coordinates: coords, optimize: false});
            console.log(resp);
            if (!resp.data.geojson || !resp.data.geojson.legs) {
                console.error("No route found");
                setLoading(false);
                return;
            }

            let times = 0;
            const unload = (panel.unloading || 0) * 60;
            const items = [waypoints[0]];

            resp.data.geojson.legs.forEach((leg, idx) => {
                const index = idx + 1;
                const item_ = {...waypoints[index]};
                if (item_.is_asset) {
                    times += unload;
                    item_.step = leg.duration + unload;
                } else {
                    times += (item_.waypoint_time || 0) * 60;
                    item_.step = leg.duration + (item_.waypoint_time || 0) * 60;
                }
                times += leg.duration;
                items.push(item_);
            });

            setWaypoints(items);
            setDuration(Math.ceil(times));
            setGeojson(resp.data.geojson);
            setHasChanged(false);
            paintRoute(resp.data.geojson);
        } catch (error) {
            console.error("Error calculating route:", error);
        } finally {
            setLoading(false);
        }
    };
    const clearMarkers = () => {
        Object.values(markersRef.current).forEach((mk) => {
            if (map && map.markers) {
                try { map.markers.remove(mk); } catch (e) {}
            }
        });
        markersRef.current = {};
    };

    const addAssetsMap = () => {
        if (!map) return;

        // 1. Limpiamos marcadores HTML previos
        clearMarkers();
        htmlContentRef.current = {};

        // 2. Si usamos un popup compartido, lo cerramos antes de empezar
        if (sharedPopup.current) sharedPopup.current.close();

        const points = [];

        waypoints.forEach((point, index) => {
            // Validación de coordenadas
            if (typeof point.lng !== "number" || typeof point.lat !== "number" || isNaN(point.lng) || isNaN(point.lat)) {
                return;
            }

            const html = point.is_asset
                ? getWasteIconHTML(point.waste_type || routeWasteType, point.filling || point.island_filling)
                : getFontAwesomeIconHTML(point.waypoint_icon);

            if (!html) return;

            const position = [point.lng, point.lat];
            points.push(position);

            const marker = new HtmlMarker({
                htmlContent: html,
                position: position,
                pixelOffset: [0, 0],
            });

            // Evento con el popup compartido
            map.events.add("click", marker, () => {
                const popupContent = point.is_asset ? `UID: ${point.uid}` : point.waypoint_name;
                sharedPopup.current.setOptions({
                    content: `<div style="padding:10px; color: black;">${popupContent}</div>`,
                    position: position,
                });
                sharedPopup.current.open(map);
            });

            if (map.markers) map.markers.add(marker);
            const idKey = String(point.id ?? point.asset_id ?? point.waypoint_id ?? point.uid ?? index);
            markersRef.current[idKey] = marker;
            htmlContentRef.current[idKey] = html;
        });

        // 3. Ajuste de cámara (FitBounds)
        if (points.length > 0) {
            map.setCamera({
                // Ojo: verifica si es 'atlas' o 'atlasData' según tu importación
                bounds: atlasData.BoundingBox.fromPositions(points),
                padding: 50,
                type: "jump",
            });
        }
    };

    useEffect(() => {
        if (!map) return;

        if (!highlightedPoint) {
            Object.entries(markersRef.current).forEach(([key, mk]) => {
                const orig = htmlContentRef.current[key];
                if (orig) mk.setOptions({htmlContent: orig});
            });
            return;
        }

        const id = String(highlightedPoint);
        Object.entries(markersRef.current).forEach(([key, mk]) => {
            const orig = htmlContentRef.current[key];
            if (!orig) return;
            if (key === id) {
                mk.setOptions({htmlContent: orig});
            } else {
                mk.setOptions({htmlContent: `<div style="opacity:0.2;pointer-events:none">${orig}</div>`});
            }
        });

        const activeMk = markersRef.current[id];
        if (activeMk) {
            const pos = activeMk.getOptions().position;
            try { map.setCamera({center: pos, type: "ease", duration: 300}); } catch (e) {}
        }
    }, [highlightedPoint, waypoints]);

    const paintRoute = (data) => {
        if (!map || !linesSourceRef.current) return;
        linesSourceRef.current.clear();
        if (!data) return;
        if (!data?.geometry?.coordinates?.length) return;

        const allCoords = data.geometry.coordinates.flatMap((step) => step.map(([lng, lat]) => [lng, lat]));
        if (allCoords.length < 2) return;

        // Añadir la línea al DataSource
        linesSourceRef.current.setShapes([new atlasData.LineString(allCoords)]);

        // Ajustar la cámara (FitBounds)
        map.setCamera({
            bounds: atlasData.BoundingBox.fromPositions(allCoords),
            padding: 50,
            type: "jump",
        });
    };

    const onMapReady = (mapInstance) => {
        setMap(mapInstance);
        linesSourceRef.current = new source.DataSource();
        mapInstance.sources.add(linesSourceRef.current);
        const lineLayer = new layer.LineLayer(linesSourceRef.current, null, {
            strokeColor: "#3388ff",
            strokeWidth: 5,
            strokeOpacity: 0.6,
            filter: ["==", ["geometry-type"], "LineString"],
        });

        mapInstance.layers.add(lineLayer);
    };

    return (
        <div style={{height: "100%", width: "100%", position: "relative"}}>
            <div className="row g-3 mb-3">
                <div className="col-3 text-end">
                    <label htmlFor="description" className="col-form-label">
                        {tF("routes.duration")}
                    </label>
                </div>
                <div className="col-9">
                    <input
                        className="form-control disabled"
                        id="duration"
                        name="duration"
                        value={DurationDisplay({seconds: duration, format: "HH:mm"})}
                        readOnly
                        style={{width: "90px", backgroundColor: "#e9ecef", opacity: 1}}
                    />
                </div>
            </div>
            {hasChanged && waypoints.length > 1 ? (
                <div className="col-12 text-end">
                    <button type="button" className="btn btn-primary mb-3" onClick={calculateRoute}>
                        {tF("routes.calculate-route")}
                    </button>
                </div>
            ) : (
                <></>
            )}

            <div style={{height: "90%", width: "100%", position: "relative"}}>
                <AzureMapBase center={center} zoom={13} height="100%" onMapReady={onMapReady} />
                <Loader show={loading} />
            </div>
        </div>
    );
};

export default AzureMapsRouteCalculator;
